import { ColumnsType, ColumnType } from 'antd/es/table';
import {
  CalculationPickingIssuesResponse,
  CalculationStatisticStoreSourceResponse,
  PickingIssueInsightsResponse,
  PickingIssueResponse,
  StorePickingInsightsResponse,
} from '@ydistri/api-sdk';
import { OutlierValue, YColumnsType } from '@ydistri/ds';
import { formatNumber } from '@ydistri/generic-utils';
import { isOutlier } from './pickingIssuesUtilsLib';
import { computeRemSize } from '@ydistri/theme';

export type PickingIssuesData = CalculationStatisticStoreSourceResponse &
  CalculationPickingIssuesResponse &
  Pick<StorePickingInsightsResponse, 'executedQuantity' | 'executedValue' | 'pickingPositionCount'>;

/**
 * Mapping of the issue names to the corresponding string for UI.
 * If the issue name is not in the map, it's name will be used as is.
 * */
const issuesMap = new Map<string, string>([
  ['DamagedOrExpired', 'Damaged / Expired'],
  ['NotSending', 'Not Sending More'],
  ['NotFound', 'Not Found'],
  ['DifferentSupply', 'Different Supply'],
  ['skipTemporarily', 'Skipped Temporarily'],
  ['SkipPermanently', 'Skipped Permanently'],
  ['NotInPlanogram', 'Not In Planogram'],
]);

//map of columns for each of the redistribution types
const issueColumnsMap = new Map<string, ColumnType<PickingIssuesData>>();

/**
 * Translate the issue type to the corresponding string for UI
 * @param issueName
 */
export const translateRedistributionIssue = (issueName: string): string => {
  return issuesMap.get(issueName) ?? issueName;
};

/**
 * Sorts issues by translated title alphabetically, type Other is always last as it means none of the others
 * @param left
 * @param right
 */
const issueTypeSorter = (left: string, right: string): number => {
  if (left === 'Other') {
    return 1;
  }

  if (right === 'Other') {
    return -1;
  }

  return translateRedistributionIssue(left).localeCompare(translateRedistributionIssue(right));
};
/**
 * Combine the data from the calculation statistic, picking issues and picking insights to one object
 * that can be used in the table component
 * @param stats
 * @param issues
 * @param pickingInsights
 */
export const preparePickingIssuesData = (
  stats: CalculationStatisticStoreSourceResponse[],
  issues: CalculationPickingIssuesResponse[],
  pickingInsights: StorePickingInsightsResponse[],
): PickingIssuesData[] => {
  return issues.map(issue => {
    const matchedStat = stats.find(stat => stat.sourceStore.id === issue.store.id);
    const matchedInsight = pickingInsights.find(insight => insight.store.id === issue.store.id);

    const result: PickingIssuesData = {
      store: issue.store,
      sourceStore: matchedStat ? matchedStat.sourceStore : issue.store,
      issues: issue.issues,
      value: matchedStat ? matchedStat.value : 0,
      quantity: matchedStat ? matchedStat.quantity : 0,
      productCount: matchedStat ? matchedStat.productCount : 0,
      pickingPositionCount: matchedStat ? matchedStat.pickingPositionCount : 0,
      targetStoreCount: matchedStat ? matchedStat.targetStoreCount : 0,
      count: issue.count,
      executedQuantity: matchedInsight ? matchedInsight.executedQuantity : 0,
      executedValue: matchedInsight ? matchedInsight.executedValue : 0,
    };

    return result;
  });
};

/**
 * Get the number of issues of a specific type from the list of issues
 * @param issues
 * @param issueName
 */
const getIssueCount = (
  issues: PickingIssueInsightsResponse[],
  issueName: string,
): number | undefined => {
  const issue = issues.find(issue => issue.papRedistributionIssue.name === issueName);
  if (issue) {
    return issue.count;
  }
};

/**
 * Render the cell for a specific issue type.
 * If the issue is an outlier, the value will be displayed in a different style to draw attention to it.
 * @param record
 * @param issueName
 */
const issueCellRenderer = (record: CalculationPickingIssuesResponse, issueName: string) => {
  const value = getIssueCount(record.issues, issueName);
  if (value) {
    const allValues = record.issues.map(issue => issue.count);
    const outlier = isOutlier(allValues, value);
    return outlier ? (
      <OutlierValue
        value={value}
        tooltip={`Number of issues of type '${issueName}' is higher than issues of other types in store ${record.store.name}`}
        center
      />
    ) : (
      value
    );
  }
};

/**
 * Generate a column for given issue type.
 * Columns for issue type have the same properties and behave the same way
 * @param issueName
 */
const generateIssueColumn = (issueName: string): ColumnType<PickingIssuesData> => {
  return {
    title: translateRedistributionIssue(issueName),
    key: issueName,
    align: 'center',
    width: computeRemSize(100),
    render: (record: CalculationPickingIssuesResponse) => issueCellRenderer(record, issueName),
  };
};

/**
 * Generate a map of columns for issue names
 * Clears the previous columns if the map is not empty
 */
const generateIssueColumnsMap = (usedIssueNames: Set<string>) => {
  if (issueColumnsMap.size > 0) {
    issueColumnsMap.clear();
  }

  usedIssueNames.forEach(issueName => {
    issueColumnsMap.set(issueName, generateIssueColumn(issueName));
  });
};

/**
 * Generate the columns for the picking issues table
 * @param data
 * @param currency
 * @param storeColumns The columns for the store
 */
export const getPickingIssuesColumns = (
  data: PickingIssuesData[],
  currency: string,
  storeColumns: YColumnsType<PickingIssuesData>[],
): ColumnsType<PickingIssuesData> => {
  const columns: ColumnsType<PickingIssuesData> = [...storeColumns];
  columns.push(
    {
      title: 'Quantity',
      key: 'quantity',
      align: 'right',
      width: computeRemSize(120),
      render: (record: PickingIssuesData) => {
        const diff = record.quantity - record.executedQuantity;
        const value = `${record.executedQuantity} / ${record.quantity}   ( ${formatNumber(-diff)} )`;

        const observations = data.map(record => record.quantity - record.executedQuantity);
        const outlier = isOutlier(observations, diff);
        return outlier ? (
          <OutlierValue
            value={value}
            tooltip="Difference between requested and executed QUANTITY is higher than in other stores"
            center
          />
        ) : (
          value
        );
      },
      sorter: (a: PickingIssuesData, b: PickingIssuesData) => {
        //when sorted in ascending order, the smallest difference should be first
        //so diff is a positive number
        const diffA = a.quantity - a.executedQuantity;
        const diffB = b.quantity - b.executedQuantity;

        return diffA - diffB;
      },
    },
    {
      title: 'Value',
      key: 'value',
      align: 'right',
      width: computeRemSize(180),
      render: (record: PickingIssuesData) => {
        const diff = record.value - record.executedValue;
        const value = `${formatNumber(record.executedValue)} / ${formatNumber(record.value)}   ( ${formatNumber(-diff)} )`;
        const observations = data.map(record => record.value - record.executedValue);
        const outlier = isOutlier(observations, diff);
        return outlier ? (
          <OutlierValue
            value={value}
            tooltip="Difference between requested and executed VALUE is higher than in other stores"
            center
          />
        ) : (
          value
        );
      },
      sorter: (a: PickingIssuesData, b: PickingIssuesData) => {
        //when sorted in ascending order, the smallest difference should be first
        //so diff is a positive number
        const diffA = a.value - a.executedValue;
        const diffB = b.value - b.executedValue;

        return diffA - diffB;
      },
    },
    {
      title: 'Picking Positions',
      dataIndex: 'pickingPositionCount',
      key: 'pickingPositionsCount',
      align: 'right',
      width: computeRemSize(100),
    },
    {
      title: 'Total Issues',
      dataIndex: 'count',
      key: 'totalIssuesCount',
      align: 'center',
      width: computeRemSize(100),
      render: (value: number) => {
        const observations = data.map(record => record.count);
        const outlier = isOutlier(observations, value);
        return outlier ? (
          <OutlierValue
            value={value}
            tooltip="Total issues count is higher than in other stores"
            center
            colorVariant="orange"
          />
        ) : (
          value
        );
      },
      sorter: (a: PickingIssuesData, b: PickingIssuesData) => a.count - b.count,
    },
  );

  /****** Dynamically add columns for used issues only ******************/
  //determine what issues are used in dataset
  const usedIssueNames = new Set<string>();
  data.forEach(record => {
    record.issues.forEach(issue => usedIssueNames.add(issue.papRedistributionIssue.name));
  });

  //if the column definitions were not created yet, created them
  if (issueColumnsMap.size === 0) {
    generateIssueColumnsMap(usedIssueNames);
  }

  //add columns for used issues only
  Array.from(usedIssueNames)
    .sort(issueTypeSorter) //sort by title alphabetically, Other is always last
    .forEach(issueType => {
      const column = issueColumnsMap.get(issueType);
      if (column) {
        columns.push(column);
      }
    });

  return columns;
};

/**
 * Generate the columns for the picking issues of a single source store table
 * @param targetStoreColumns columns for the target store based on unified rules
 * @param productColumns columns for product
 */
export const getSourceStorePickingIssuesColumns = (
  targetStoreColumns: YColumnsType<PickingIssueResponse>[],
  productColumns: YColumnsType<PickingIssueResponse>[],
): ColumnsType<PickingIssueResponse> => {
  const columns: ColumnsType<PickingIssueResponse> = [
    ...productColumns,
    {
      title: 'Brand',
      dataIndex: ['product', 'brand', 'name'],
      sorter: (a: PickingIssueResponse, b: PickingIssueResponse) => {
        return a.product.brand?.name.localeCompare(b.product.brand?.name ?? '') ?? 0;
      },
    },
  ];

  columns.push(...targetStoreColumns);

  columns.push(
    {
      title: 'Issue',
      key: 'issue',
      width: computeRemSize(140),
      sorter: (a: PickingIssueResponse, b: PickingIssueResponse) => {
        const issueA = translateRedistributionIssue(a.papRedistributionIssue.name);
        const issueB = translateRedistributionIssue(b.papRedistributionIssue.name);
        return issueA.localeCompare(issueB);
      },
      render: (record: PickingIssueResponse) =>
        translateRedistributionIssue(record.papRedistributionIssue.name),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      align: 'right',
      width: computeRemSize(120),
      render: (record: PickingIssueResponse) => {
        const diff = record.quantityExecuted - record.quantityRequested;
        return `${record.quantityExecuted} / ${record.quantityRequested}   ( ${formatNumber(diff)} )`;
      },
    },
  );

  return columns;
};
